/**
 * AdditionalServicesStep Organism Component
 * Step 2 of reservation wizard - Insurance and additional services
 */

'use client'

import { DiscountCodeInput, ServiceCard } from '@/components/molecules'
import { Button } from '@/components/ui/button'
import { useExtras } from '@/hooks/useExtras'
import {
	calculateAdditionalServicesTotal,
	getAdditionalServiceUnitPriceWithTax,
	getReservationDays,
} from '@/lib/reservation-pricing'
import { cn } from '@/lib/utils'
import { tattersallService } from '@/services/tattersall'
import { useReservationStore } from '@/store'
import type { AdditionalService, ReservationExtras } from '@/types/reservation'
import { useEffect, useMemo, useState } from 'react'

interface AdditionalServicesStepProps {
	carId?: string
	days?: number
	afterHoursSurchargeTotal?: number
	onContinue?: () => void
	onBack?: () => void
	className?: string
}

export function AdditionalServicesStep({
	days,
	afterHoursSurchargeTotal = 0,
	onContinue,
	onBack,
	className,
}: AdditionalServicesStepProps) {
	const {
		currentReservation,
		setExtras,
		setDiscountCode,
		setSearchParams,
		searchParams,
		selectedCar,
		twhSelected,
		setTwhSelected,
	} = useReservationStore()

	const { extras, isLoading } = useExtras({
		datePickUp: searchParams.pickupDate,
		stationPickUp: searchParams.pickupStationId,
		dateReturn: searchParams.dropoffDate,
		stationReturn: searchParams.dropoffStationId,
		groupId: selectedCar?.groupID || '',
	})

	const [services, setServices] = useState<AdditionalService[]>([])
	const [discountCode, setLocalDiscountCode] = useState(
		currentReservation?.extras?.discountCode || '',
	)
	const [couponValidation, setCouponValidation] = useState<{
		isValid?: boolean
		message?: string
		discountAmount?: number
	}>({})

	const reservationDays = useMemo(
		() =>
			days ??
			getReservationDays(searchParams.pickupDate, searchParams.dropoffDate),
		[days, searchParams.pickupDate, searchParams.dropoffDate],
	)

	// ID especial para el extra sintético THW que se añade cuando la oferta tiene variante
	const THW_SYNTHETIC_ID = '__THW__'

	// Update services when extras are fetched
	useEffect(() => {
		if (extras.length > 0) {
			const seen = new Set<string>()
			const mappedServices: AdditionalService[] = extras
				.filter(extra => {
					if (seen.has(extra.extraID)) return false
					seen.add(extra.extraID)
					return true
				})
				.map(extra => ({
					id: extra.extraID,
					name: extra.extra,
					description: extra.description,
					price: extra.value,
					taxRate: (extra.taxRate || 0) / 100, // convert % integer → decimal (e.g. 19 → 0.19)
					currency: extra.extraCurrency,
					priceType: extra.extraByDay ? 'daily' : 'total',
					selected:
						extra.extra_Required ||
						extra.extra_Included ||
						currentReservation?.extras?.additionalServices?.find(
							s => s.id === extra.extraID,
						)?.selected ||
						false,
					category: extra.insurance ? 'insurance' : 'service',
					included: extra.extra_Included,
					required: extra.extra_Required,
				}))

			// Si el vehículo tiene variante THW, inyectarla como extra opcional
			// (si no está ya en los extras del vehículo base)
			const hasThwInExtras = mappedServices.some(
				s => s.id?.toUpperCase() === 'THW',
			)
			if (selectedCar?.twhOffer && !hasThwInExtras) {
				const thwOffer = selectedCar.twhOffer
				// Calcular el precio incremental THW = precio total con THW − precio base
				const thwTotalPrice =
					thwOffer.previewValueWithDiscount ??
					thwOffer.previewValue ??
					thwOffer.dayValue * thwOffer.nrDays
				const baseTotalPrice =
					selectedCar.pricing.weeklyRate ??
					selectedCar.pricing.dailyRate * reservationDays
				const thwIncrement = Math.max(
					0,
					Math.round(thwTotalPrice - baseTotalPrice),
				)

				if (thwIncrement === 0) {
					// Mismo precio que la oferta base → THW gratuito.
					// Auto-seleccionar silenciosamente (cobertura superior sin costo).
					setTwhSelected(true)
					mappedServices.unshift({
						id: THW_SYNTHETIC_ID,
						name: 'Protección Total THW',
						description:
							'Cobertura ampliada contra robo total del vehículo. Sin franquicia. Incluida en tu tarifa.',
						price: 0,
						taxRate: 0,
						currency: thwOffer.currency || 'COP',
						priceType: 'total',
						selected: true,
						category: 'insurance',
						included: true,
						required: true,
					})
				} else {
					// THW tiene costo adicional → mostrar como extra opcional
					mappedServices.unshift({
						id: THW_SYNTHETIC_ID,
						name: 'Protección Total THW',
						description:
							'Cobertura ampliada contra robo total del vehículo. Sin franquicia.',
						price: thwIncrement,
						taxRate: 0,
						currency: thwOffer.currency || 'COP',
						priceType: 'total',
						selected: twhSelected,
						category: 'insurance',
						included: false,
						required: false,
					})
				}
			}

			setServices(mappedServices)
		}
	}, [
		extras,
		currentReservation?.extras?.additionalServices,
		selectedCar,
		twhSelected,
		reservationDays,
	])

	const handleToggleService = (serviceId: string) => {
		const updatedServices = services.map(s => {
			// Don't toggle required services
			if (s.id === serviceId && s.required) return s

			return s.id === serviceId ? { ...s, selected: !s.selected } : s
		})

		setServices(updatedServices)

		// Si es el extra sintético THW, actualizar el flag en el store
		if (serviceId === THW_SYNTHETIC_ID) {
			const nowSelected =
				updatedServices.find(s => s.id === THW_SYNTHETIC_ID)?.selected ?? false
			setTwhSelected(nowSelected)
		}

		// Sync to store immediately to update widget
		const selectedExtrasList = extras
			.filter(extra =>
				updatedServices.find(s => s.id === extra.extraID && s.selected),
			)
			.map(extra => {
				const matchedService = updatedServices.find(s => s.id === extra.extraID)

				return {
					extraId: extra.extraID,
					extraCode: extra.extraID,
					extraName: extra.extra,
					description: extra.description,
					dailyPrice: matchedService
						? getAdditionalServiceUnitPriceWithTax(matchedService)
						: extra.value,
					currency: extra.extraCurrency,
					mandatory: extra.extra_Required,
					maxQuantity: extra.accept_quantity ? 10 : 1,
				}
			})

		const updatedExtras: ReservationExtras = {
			additionalDriver: currentReservation?.extras?.additionalDriver || false,
			gps: currentReservation?.extras?.gps || false,
			childSeat: currentReservation?.extras?.childSeat || false,
			additionalInsurance:
				currentReservation?.extras?.additionalInsurance || false,
			...currentReservation?.extras,
			additionalServices: updatedServices,
			selectedExtras: selectedExtrasList,
			discountCode: discountCode,
			discountAmount: currentReservation?.extras?.discountAmount || 0,
		}
		setExtras(updatedExtras)
	}

	const handleApplyDiscount = async (code: string) => {
		const normalizedCode = code.trim().toUpperCase()

		const clearDiscount = () => {
			setDiscountCode('')
			setSearchParams({ promotionalCode: '' })
			setExtras({
				additionalDriver: false,
				gps: false,
				childSeat: false,
				additionalInsurance: false,
				...currentReservation?.extras,
				additionalServices: services,
				discountCode: '',
				discountAmount: 0,
			})
		}

		try {
			const offersWithCoupon = await tattersallService.searchVehicles({
				...searchParams,
				promotionalCode: normalizedCode,
			})

			const matchedOffer = offersWithCoupon.find(
				offer =>
					offer.groupID === selectedCar?.groupID ||
					offer.group_Name === selectedCar?.description ||
					`${offer.groupDetail?.brand || ''} ${offer.groupDetail?.model || ''}`
						.trim()
						.toLowerCase() ===
						`${selectedCar?.brand || ''} ${selectedCar?.model || ''}`
							.trim()
							.toLowerCase(),
			)

			if (!matchedOffer) {
				clearDiscount()
				setCouponValidation({
					isValid: false,
					message: 'Código no válido para las tarifas actuales',
					discountAmount: 0,
				})
				return
			}

			// Compare the promo price against the UNDISCOUNTED original price.
			// previewValue is always the price before any promo code; previewValueWithDiscount
			// may already be reduced if the initial search included a promotional code, which
			// would cause a false "no discount" result when the code is re-validated here.
			const originalOffer = selectedCar?.originalOffer
			const originalPrice =
				originalOffer?.previewValue ||
				originalOffer?.previewValueWithDiscount ||
				selectedCar?.pricing.weeklyRate ||
				0

			// promoPrice: prefer previewValueWithDiscount, then dayValueWithDiscount×nrDays,
			// then previewValue. Never fall straight to previewValue before trying the daily rate.
			const promoPrice =
				matchedOffer.previewValueWithDiscount ||
				(matchedOffer.dayValueWithDiscount
					? matchedOffer.dayValueWithDiscount * matchedOffer.nrDays
					: null) ||
				matchedOffer.previewValue ||
				0

			// Round each value independently before subtracting, so that the resulting
			// promoDiscount is consistent with roundCurrency(vehicleTotal) used in
			// buildReservationPriceBreakdown. This ensures:
			//   roundCurrency(vehicleTotal) - promoDiscount === Math.round(promoPrice)
			// Rounding the difference Math.round(a - b) can differ by ±1 from Math.round(a)
			// - Math.round(b) when the decimal parts of a or b are near 0.5.
			const promoDiscount = Math.max(
				0,
				Math.round(originalPrice) - Math.round(promoPrice),
			)
			const percentDiscount = matchedOffer.percentualDiscount || 0

			// Require at least $500 COP discount to avoid false positives from API rounding differences
			if (promoDiscount >= 500) {
				setLocalDiscountCode(normalizedCode)
				setDiscountCode(normalizedCode)
				setSearchParams({ promotionalCode: normalizedCode })
				setExtras({
					additionalDriver: false,
					gps: false,
					childSeat: false,
					additionalInsurance: false,
					...currentReservation?.extras,
					additionalServices: services,
					discountCode: normalizedCode,
					discountAmount: promoDiscount,
				})
				setCouponValidation({
					isValid: true,
					message:
						percentDiscount > 0
							? `Cupón aplicado (${percentDiscount}% de descuento)`
							: 'Cupón aplicado',
					discountAmount: promoDiscount,
				})
				return
			}

			// Offer found but price unchanged — code not recognized by API
			clearDiscount()
			setCouponValidation({
				isValid: false,
				message: 'El código no aplica a las tarifas actuales',
				discountAmount: 0,
			})
		} catch {
			clearDiscount()
			setCouponValidation({
				isValid: false,
				message: 'Error al validar el cupón. Intenta nuevamente.',
				discountAmount: 0,
			})
		}
	}

	const handleContinue = () => {
		// Filter selected extras from the API response based on user selection
		const selectedExtrasList = extras
			.filter(extra => services.find(s => s.id === extra.extraID && s.selected))
			.map(extra => {
				const matchedService = services.find(
					service => service.id === extra.extraID,
				)

				return {
					extraId: extra.extraID,
					extraCode: extra.extraID,
					extraName: extra.extra,
					description: extra.description,
					dailyPrice: matchedService
						? getAdditionalServiceUnitPriceWithTax(matchedService)
						: extra.value,
					currency: extra.extraCurrency,
					mandatory: extra.extra_Required,
					maxQuantity: extra.accept_quantity ? 10 : 1,
				}
			})

		// Save selected services to store
		const updatedExtras: ReservationExtras = {
			additionalDriver: currentReservation?.extras?.additionalDriver || false,
			gps: currentReservation?.extras?.gps || false,
			childSeat: currentReservation?.extras?.childSeat || false,
			additionalInsurance:
				currentReservation?.extras?.additionalInsurance || false,
			additionalServices: services,
			selectedExtras: selectedExtrasList,
			discountCode: discountCode,
			discountAmount: currentReservation?.extras?.discountAmount || 0,
		}
		setExtras(updatedExtras)

		onContinue?.()
	}

	const selectedServices = services.filter(s => s.selected)
	const totalExtras = calculateAdditionalServicesTotal(
		selectedServices,
		reservationDays,
	)
	const includedServices = services.filter(s => s.included || s.required)
	const additionalServices = services.filter(s => !s.included && !s.required)

	const formatCurrency = (amount: number) => {
		return new Intl.NumberFormat('es-CO', {
			style: 'currency',
			currency: 'COP',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(amount)
	}

	if (isLoading) {
		return (
			<div className='flex justify-center py-12'>
				<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#09194D]'></div>
			</div>
		)
	}

	return (
		<div className={cn('flex flex-col gap-8 py-8 px-[3%]', className)}>
			{/* Include Section */}
			{includedServices.length > 0 && (
				<section>
					<div className='bg-[#09194D] inline-block px-6 py-2 mb-4'>
						<h2 className='font-["Bricolage_Grotesque"] text-xl font-black text-[#CDFF00]'>
							Incluye
						</h2>
					</div>

					<div className='bg-white space-y-6'>
						{includedServices.map(service => (
							<div key={service.id}>
								<h3 className='font-["Bricolage_Grotesque"] text-3xl font-bold text-[#09194D] mb-2'>
									{service.name}
								</h3>
								<div className='text-sm text-[#09194D]/80 space-y-1'>
									<p>{service.description}</p>
								</div>
							</div>
						))}
					</div>
				</section>
			)}

			{/* Additional Services Section */}
			<section>
				<div className='inline-block py-2 mb-4'>
					<h2 className='font-["Bricolage_Grotesque"] text-3xl font-black text-[#09194D]'>
						Adicionales
					</h2>
					<p className='mt-1 text-sm text-[#09194D]/70'>
						Valores con IVA incluido
					</p>
				</div>

				<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
					{additionalServices.map(service => (
						<ServiceCard
							key={service.id}
							title={service.name}
							description={service.description}
							price={getAdditionalServiceUnitPriceWithTax(service)}
							priceType={service.priceType}
							isSelected={service.selected}
							onToggle={() => handleToggleService(service.id)}
						/>
					))}
				</div>
			</section>

			{/* Discount Code Section */}
			<section>
				<DiscountCodeInput
					onApply={handleApplyDiscount}
					appliedCode={discountCode}
					validationMessage={couponValidation.message}
					isValid={couponValidation.isValid}
					discountAmount={couponValidation.discountAmount}
				/>
			</section>

			{/* Navigation Buttons */}
			<div className='flex items-end justify-between gap-4 pt-6 border-t-2 border-gray-200'>
				<Button
					onClick={onBack}
					variant='outline'
					className='border-2 border-[#09194D] bg-white hover:bg-[#09194D] hover:text-white text-[#09194D] font-["Bricolage_Grotesque"] font-bold text-lg px-8 py-3 h-auto rounded-full cursor-pointer transition-colors'
				>
					Regresar
				</Button>

				<div className='flex flex-col items-end gap-2'>
					{(totalExtras > 0 || afterHoursSurchargeTotal > 0) && (
						<div className='text-right'>
							<p className='text-sm text-[#09194D]/70'>
								Total adicionales IVA incluido:
							</p>
							<p className='font-["Bricolage_Grotesque"] text-2xl font-black text-[#09194D]'>
								{formatCurrency(totalExtras + afterHoursSurchargeTotal)}
							</p>
						</div>
					)}
					{couponValidation.isValid && couponValidation.discountAmount && (
						<div className='text-right'>
							<p className='text-sm text-green-700'>Descuento aplicado:</p>
							<p className='font-["Bricolage_Grotesque"] text-2xl font-black text-green-700'>
								-{formatCurrency(couponValidation.discountAmount)}
							</p>
						</div>
					)}
					<Button
						onClick={handleContinue}
						className='bg-[#CDFF00] hover:bg-[#b0db00] text-[#09194D] font-["Bricolage_Grotesque"] font-black text-2xl px-12 py-3 h-auto rounded-full cursor-pointer transition-colors'
					>
						Continuar
					</Button>
				</div>
			</div>
		</div>
	)
}
